import { useWindowSize } from "hooks/useWindowSize";
import { HeaderActions } from "components/AppLayout/HeaderActions";
import classNames from "classnames";
import { CommentsSlider } from "components/CommentsSlider/CommentsSlider";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { ExportOptions } from "components/ExportOptions/ExportOptions";
import { StartBookkeepingDateEmptyScreen } from "components/GeneralLedger/GeneralLedgerEmptyScreen";
import { MoneyInTransitBanner } from "components/MoneyTransitBanner/MoneyInTransitBanner";
import { SliderAnimation } from "components/SliderAnimation/SliderAnimation";
import { TransactionSlider } from "components/Transaction/Slider/TransactionSlider";
import { ConditionalLink } from "components/conditionalLink";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { ArrowDown } from "components/icons/ArrowDown";
import { ChatTeardropDots } from "components/icons/ChatTeardropDots";
import { Redirect } from "components/icons/Redirect";
import {
  BALANCE_SHEET,
  CASH_FLOW_STATEMENT,
  DOWNLOAD_FORMAT,
  DOWNLOAD_FORMAT_TYPES,
  INCOME_STATEMENT,
  SAVED_REPORTS,
} from "constants/bookkeeping";
import { YYYY_MM_DD } from "constants/date";
import * as DATE_PERIOD from "constants/dateFilter";
import { PERIOD } from "constants/revenueMetrics";
import dayjs from "dayjs";
import { REPORT_TYPE } from "dictionaries";
import { AnimatePresence } from "framer-motion";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { UpdateFilterFunction, useFilters } from "hooks/useFilter";
import { useModal } from "hooks/useModal";
import { usePageTitle } from "hooks/usePageTitle";
import { useQuery } from "hooks/useQuery";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { parse, stringify } from "qs";
import { useHistory, useLocation } from "react-router-dom";
import { useToast } from "hooks/useToast";
import { ReportsType } from "pages/Books/Reports/Reports";
import { useEffect, useState } from "react";
import { useGetMoneyInTransitQuery } from "store/apis/generalLedger";
import { NoteResponse } from "store/apis/notes";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import {
  useLazyNewInteractiveReportQuery,
  useNewInteractiveReportQuery,
  useSaveInteractiveReportMutation,
} from "store/apis/reports";
import { setOpenComment } from "store/slices/openComment";
import { BackendError } from "types/utils/error";
import { getDateRange, getDateRangeWithSetting } from "utils/getDateRange";
import { openLink } from "utils/openLink";
import { redirectToCategories } from "utils/redirectToCategories";
import { AddToClosing } from "./AddToClosing";
import { ComparisonReport } from "./ComparisonReport";
import { RefreshButton } from "components/DesignSystem/RefreshButton/RefreshButton";
import { REFRESH_PAGE } from "constants/refreshPage";
import { VarianceReportDownload } from "./VarianceExplanation/VarianceReportDownload";
import { ReportFilter, ReportFilterType } from "./ReportFilter";
import ReportsAccordion, { REPORT_COMMENT_TYPE } from "./ReportsAccordion";
import {
  BooksLockDateResponse,
  useGetBookkeepingSettingsQuery,
} from "store/apis/booksLockDate";
import { AccountingMethodBanner } from "components/BooksSettings/AccountingMethodBanner";
import { TableSelectionControls } from "components/GeneralLedger/TableSelectionControls";
import { SelectFolderForUpload } from "components/SelectFolderForUpload";
import { SavedToInkleDocsModal } from "components/SavedToInkleDocsModal/SavedToInkleDocsModal";
import { useGetStartingDateQuery } from "store/apis/chartOfAccounts";
import { useCurrentGroup } from "hooks/useCurrentGroup";
import Loader from "components/DesignSystem/Loader/loader";
import { ForecastToolbar } from "components/Forecast/ForecastToolbar";
import {
  ForecastComparisonReport,
  ForecastReportDownload,
} from "./ForecastComparisonReport";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";

const REPORT_REFRESH_ENDPOINTS = [
  "newInteractiveReport",
  "byViewInteractiveReport",
  "getForecastReport",
];

const ReportContent = ({
  reportType,
  isGroupByView,
  filter,
}: {
  reportType: ReportsType;
  isGroupByView: boolean;
  filter: ReportFilterType;
}) => {
  if (filter.FORECAST_ENABLED && isGroupByView) {
    return <ForecastComparisonReport reportType={reportType} filter={filter} />;
  }
  if (isGroupByView) {
    return <ComparisonReport filter={filter} reportType={reportType} />;
  }
  return <ReportView filter={filter} reportType={reportType} />;
};

const ExportChevron = ({ isOpen }: { isOpen: boolean }) => (
  <span
    className={classNames("t-transition-transform t-duration-200", {
      "t-rotate-180": isOpen,
    })}
  >
    <ArrowDown color="currentColor" strokeWidth="1.5" size="16" />
  </span>
);

export const ReportDownload = ({
  reportType,
  isGroupByView,
  documentId,
  excelDocumentId,
  filter,
}: {
  reportType: ReportsType;
  isGroupByView?: boolean;
  documentId?: string | null;
  excelDocumentId?: string | null;
  filter?: ReportFilterType;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();
  const { isAdmin } = useRoleBasedView();
  const isFinancialStatement = [
    BALANCE_SHEET,
    INCOME_STATEMENT,
    CASH_FLOW_STATEMENT,
  ].includes(reportType);
  const [downloadReport, { isLoading: isDownloading, isFetching }] =
    useLazyNewInteractiveReportQuery();

  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();
  const selectFolderModal = useModal();
  const savedToInkleDocsModal = useModal();
  const [selectedFormat, setSelectedFormat] = useState<DOWNLOAD_FORMAT_TYPES>();
  const [savedDocumentId, setSavedDocumentId] = useState<string>();
  const [isDraftSaveToInkleDocs, setIsDraftSaveToInkleDocs] = useState(false);
  const [isDraftPdf, setIsDraftPdf] = useState(isFinancialStatement);
  const [isDraftExcel, setIsDraftExcel] = useState(isFinancialStatement);

  const onDownload = async (
    downloadType: DOWNLOAD_FORMAT_TYPES,
    exportToMail?: boolean,
    folderId?: string,
    draftWatermark?: boolean
  ) => {
    try {
      let newPdfDocumentId = draftWatermark ? null : documentId;
      let newExcelDocumentId = draftWatermark ? null : excelDocumentId;

      if (folderId) {
        const { document_id, excel_document_id } = await downloadReport({
          groupId,
          entityId,
          reportType: reportType,
          action: downloadType,
          accountingMethod: filter?.ACCOUNTING_METHOD,
          startDate: filter?.START_DATE || null,
          endDate: filter?.END_DATE || null,
          showNonZeroAccounts: filter?.HIDE_ZERO,
          comparison_report_type:
            filter?.COMPARE_BY ||
            filter?.VIEW_BY ||
            (filter?.TXN_GROUP_ID ? "CLASSIFICATION" : null),
          variance_type: filter?.VARIANCE || null,
          transaction_group_id: filter?.TXN_GROUP_ID || null,
          transaction_group_ids:
            (filter?.CLASSIFICATION || []).length > 0
              ? filter?.CLASSIFICATION.join(",")
              : null,
          class_ids: filter?.CLASS_IDS || [],
          draft_watermark: draftWatermark,
          folderId,
        }).unwrap();

        setSavedDocumentId(
          (downloadType === DOWNLOAD_FORMAT.DOWNLOAD_EXCEL
            ? excel_document_id
            : document_id)!
        );
        savedToInkleDocsModal.open();
        return;
      }

      if (
        (downloadType === DOWNLOAD_FORMAT.DOWNLOAD_EXCEL &&
          !newExcelDocumentId) ||
        (downloadType === DOWNLOAD_FORMAT.DOWNLOAD_PDF && !newPdfDocumentId)
      ) {
        const { document_id, excel_document_id } = await downloadReport({
          groupId,
          entityId,
          reportType: reportType,
          action: downloadType,
          accountingMethod: filter?.ACCOUNTING_METHOD,
          startDate: filter?.START_DATE || null,
          endDate: filter?.END_DATE || null,
          showNonZeroAccounts: filter?.HIDE_ZERO,
          export_to_mail: exportToMail,
          draft_watermark: draftWatermark,
          comparison_report_type:
            filter?.COMPARE_BY ||
            filter?.VIEW_BY ||
            (filter?.TXN_GROUP_ID ? "CLASSIFICATION" : null),
          variance_type: filter?.VARIANCE || null,
          transaction_group_id: filter?.TXN_GROUP_ID || null,
          transaction_group_ids:
            (filter?.CLASSIFICATION || []).length > 0
              ? filter?.CLASSIFICATION.join(",")
              : null,
          class_ids: filter?.CLASS_IDS || [],
        }).unwrap();
        newPdfDocumentId = document_id;
        newExcelDocumentId = excel_document_id;
      }

      if (!exportToMail) {
        const { download_url } = await getPreviewUrl({
          groupId: groupId,
          fileId:
            downloadType === DOWNLOAD_FORMAT.DOWNLOAD_EXCEL
              ? newExcelDocumentId
              : newPdfDocumentId,
        }).unwrap();

        openLink(download_url);

        successToast({ message: "Report downloaded" });
      } else {
        successToast({ message: "Report will be sent to your email" });
      }
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const pdfOptions = (
    <ExportOptions
      onLocalDownload={() =>
        onDownload(DOWNLOAD_FORMAT.DOWNLOAD_PDF, false, undefined, isDraftPdf)
      }
      onMailExport={() =>
        onDownload(DOWNLOAD_FORMAT.DOWNLOAD_PDF, true, undefined, isDraftPdf)
      }
      onSaveInInkleDocs={() => {
        setSelectedFormat(DOWNLOAD_FORMAT.DOWNLOAD_PDF);
        setIsDraftSaveToInkleDocs(isDraftPdf);
        selectFolderModal.open();
      }}
      showDraftOption={isAdmin && isFinancialStatement}
      isDraft={isDraftPdf}
      onDraftChange={setIsDraftPdf}
    />
  );

  const excelOptions = (
    <ExportOptions
      onLocalDownload={() =>
        onDownload(
          DOWNLOAD_FORMAT.DOWNLOAD_EXCEL,
          false,
          undefined,
          isDraftExcel
        )
      }
      onMailExport={() =>
        onDownload(
          DOWNLOAD_FORMAT.DOWNLOAD_EXCEL,
          true,
          undefined,
          isDraftExcel
        )
      }
      onSaveInInkleDocs={() => {
        setSelectedFormat(DOWNLOAD_FORMAT.DOWNLOAD_EXCEL);
        setIsDraftSaveToInkleDocs(isDraftExcel);
        selectFolderModal.open();
      }}
      showDraftOption={isAdmin}
      isDraft={isDraftExcel}
      onDraftChange={setIsDraftExcel}
    />
  );

  const { isMobile } = useWindowSize();
  const [isPdfOptionsOpen, setIsPdfOptionsOpen] = useState(false);
  const [isExcelOptionsOpen, setIsExcelOptionsOpen] = useState(false);

  const handleTogglePdfOptions = (event: Event) => {
    event.preventDefault();
    setIsPdfOptionsOpen((isOpen) => !isOpen);
    setIsExcelOptionsOpen(false);
  };

  const handleToggleExcelOptions = (event: Event) => {
    event.preventDefault();
    setIsExcelOptionsOpen((isOpen) => !isOpen);
    setIsPdfOptionsOpen(false);
  };

  return (
    <>
      <Dropdown.Root modal={false}>
        <Dropdown.Trigger
          asChild
          className="t-group"
          disabled={isDownloading || isFetching}
        >
          <div>
            <Button
              customType="primary"
              size="xsmall"
              onClick={(e) => e.stopPropagation()}
              isLoading={isDownloading || isFetching}
              disabled={isDownloading || isFetching}
            >
              <div className="t-flex t-items-center t-gap-1">
                Export
                <div className="group-data-state-open:-t-rotate-180 t-transform t-transition t-duration-300 t-ease-in-out">
                  <ArrowDown color="currentColor" strokeWidth="1.5" size="16" />
                </div>
              </div>
            </Button>
          </div>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content
            className="t-w-24 sm:t-w-auto sm:t-min-w-56"
            sideOffset={4}
            align="end"
            side="bottom"
            collisionPadding={12}
          >
            {isMobile ? (
              <>
                {!isGroupByView && (
                  <>
                    <Dropdown.Item onSelect={handleTogglePdfOptions}>
                      <span className="t-flex t-w-full t-items-center t-justify-between">
                        PDF
                        <ExportChevron isOpen={isPdfOptionsOpen} />
                      </span>
                    </Dropdown.Item>
                    {isPdfOptionsOpen && pdfOptions}
                  </>
                )}
                <Dropdown.Item onSelect={handleToggleExcelOptions}>
                  <span className="t-flex t-w-full t-items-center t-justify-between">
                    Excel
                    <ExportChevron isOpen={isExcelOptionsOpen} />
                  </span>
                </Dropdown.Item>
                {isExcelOptionsOpen && excelOptions}
              </>
            ) : (
              <>
                <ConditionalToolTip
                  side="left"
                  condition={
                    isGroupByView && "PDF export is not available for this view"
                  }
                >
                  <span>
                    <Dropdown.Sub>
                      <Dropdown.SubTrigger
                        className="t-outline-none hover:t-outline-none focus-visible:t-outline-none disabled:t-text-neutral-40 disabled:t-cursor-not-allowed"
                        disabled={isGroupByView}
                      >
                        PDF
                      </Dropdown.SubTrigger>
                      <Dropdown.Portal>
                        <Dropdown.SubContent
                          sideOffset={10}
                          alignOffset={-5}
                          collisionPadding={16}
                        >
                          {pdfOptions}
                        </Dropdown.SubContent>
                      </Dropdown.Portal>
                    </Dropdown.Sub>
                  </span>
                </ConditionalToolTip>

                <Dropdown.Sub>
                  <Dropdown.SubTrigger className="t-outline-none hover:t-outline-none focus-visible:t-outline-none">
                    Excel
                  </Dropdown.SubTrigger>
                  <Dropdown.Portal>
                    <Dropdown.SubContent
                      sideOffset={10}
                      alignOffset={-5}
                      collisionPadding={16}
                    >
                      {excelOptions}
                    </Dropdown.SubContent>
                  </Dropdown.Portal>
                </Dropdown.Sub>
              </>
            )}
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
      {selectFolderModal.isOpen && (
        <SelectFolderForUpload
          showModal={selectFolderModal.isOpen}
          closeModal={() => {
            setIsDraftSaveToInkleDocs(false);
            selectFolderModal.close();
          }}
          onUpload={(folderId) => {
            void onDownload(
              selectedFormat!,
              false,
              folderId,
              isDraftSaveToInkleDocs
            );
            setIsDraftSaveToInkleDocs(false);
          }}
        />
      )}
      {savedToInkleDocsModal.isOpen && savedDocumentId && (
        <SavedToInkleDocsModal
          isOpen={savedToInkleDocsModal.isOpen}
          close={savedToInkleDocsModal.close}
          fileId={savedDocumentId}
          reportType={REPORT_TYPE[reportType]}
        />
      )}
    </>
  );
};

const ReportTopBar = ({
  reportType,
  openComments,
  filter,
  updateFilter,
  isGroupByView,
}: {
  reportType: ReportsType;
  openComments: () => void;
  filter: ReportFilterType;
  updateFilter: UpdateFilterFunction<ReportFilterType>;
  isGroupByView: boolean;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();
  const { isCpa, isForeignCA, isCustomer } = useRoleBasedView();
  const addToClosingModal = useModal();
  const { isMobile } = useWindowSize();

  const { data: moneyInTransit = [] } = useGetMoneyInTransitQuery(
    {
      groupId,
      entityId,
      reportType: reportType,
      startDate: filter.START_DATE,
      endDate: filter.END_DATE,
    },
    { skip: !groupId || !entityId, refetchOnMountOrArgChange: true }
  );

  const [downloadReport, { data: interactiveReport, isFetching }] =
    useLazyNewInteractiveReportQuery();

  const onAddToMonthlyClosing = async () => {
    try {
      await downloadReport({
        groupId,
        entityId,
        reportType: reportType,
        action: isGroupByView
          ? DOWNLOAD_FORMAT.DOWNLOAD_EXCEL
          : DOWNLOAD_FORMAT.DOWNLOAD_PDF,
        accountingMethod: filter.ACCOUNTING_METHOD,
        startDate: filter.START_DATE || null,
        endDate: filter.END_DATE || null,
        showNonZeroAccounts: filter.HIDE_ZERO,
        comparison_report_type: filter.VIEW_BY || PERIOD.TOTALS,
      }).unwrap();
      addToClosingModal.open();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.FINANCIALS
  );

  const exportAndComments = (
    <>
      {filter.FORECAST_ENABLED && isGroupByView ? (
        <ForecastReportDownload filter={filter} reportType={reportType} />
      ) : (
        <ReportDownload
          filter={filter}
          reportType={reportType}
          isGroupByView={isGroupByView}
        />
      )}
      <Button customType="icon" onClick={openComments} size="small">
        <ChatTeardropDots />
      </Button>
    </>
  );

  return (
    <div className="t-flex t-flex-col t-gap-4 t-sticky t-top-0 t-bg-white t-pb-6 t-z-filter-head sm:t-static sm:t-shrink-0">
      {moneyInTransit.length > 0 && (
        <MoneyInTransitBanner transactions={moneyInTransit} />
      )}
      <div className="t-flex t-justify-between t-items-start t-w-full sm:t-flex-wrap sm:t-justify-start sm:t-items-center sm:t-gap-2 sm:t-shrink-0">
        <ReportFilter
          reportType={reportType}
          filter={filter}
          updateFilter={updateFilter}
          entityId={entityId}
          isGroupByView={isGroupByView}
          hideForecast={!hasWritePermission}
        />

        <div className="t-hidden sm:t-block sm:t-order-4 sm:t-basis-full" />
        <div className="t-flex t-gap-2 t-items-center sm:t-contents">
          <div className="t-flex sm:t-order-3">
            <RefreshButton
              endpoints={REPORT_REFRESH_ENDPOINTS}
              page={REFRESH_PAGE.REPORTS}
              shouldShowLoader
            />
          </div>
          {(isCpa || isForeignCA) && (
            <Button
              size="xsmall"
              className="sm:t-order-5"
              onClick={onAddToMonthlyClosing}
              isLoading={isFetching}
              disabled={isFetching}
            >
              Add to closing
            </Button>
          )}

          {!isMobile && exportAndComments}
        </div>
        {isMobile && <HeaderActions>{exportAndComments}</HeaderActions>}
      </div>

      {isGroupByView && (
        <ForecastToolbar filter={filter} updateFilter={updateFilter} />
      )}

      <AccountingMethodBanner
        selectedAccountingMethod={filter.ACCOUNTING_METHOD}
        entityId={entityId}
      />

      {(interactiveReport?.document_id ||
        interactiveReport?.excel_document_id) && (
        <AddToClosing
          {...addToClosingModal}
          startDate={filter.START_DATE}
          endDate={filter.END_DATE}
          key={
            filter.START_DATE && filter.END_DATE
              ? filter.START_DATE.concat(filter.END_DATE)
              : ""
          }
          reportType={reportType}
          reportFileId={
            isGroupByView
              ? interactiveReport?.excel_document_id!
              : interactiveReport?.document_id!
          }
        />
      )}
    </div>
  );
};

const ReportView = ({
  reportType,
  filter,
}: {
  reportType: ReportsType;
  filter: ReportFilterType;
}) => {
  const [selectedRows, setSelectedRows] = useState({});
  const { isAdmin } = useRoleBasedView();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.FINANCIALS
  );

  useEffect(() => {
    setSelectedRows({});
  }, [reportType]);
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const openComment = useAppSelector((store) => store.openComment.openComment);

  const { data, isLoading, isSuccess, isFetching } =
    useNewInteractiveReportQuery(
      {
        groupId,
        entityId,
        reportType: reportType,
        accountingMethod: filter.ACCOUNTING_METHOD,
        startDate: filter.START_DATE || null,
        endDate: filter.END_DATE || null,
        showNonZeroAccounts: filter.HIDE_ZERO,
        comparison_report_type: filter.VIEW_BY || PERIOD.TOTALS,
        transaction_group_id: filter.TXN_GROUP_ID || null,
        transaction_group_ids:
          (filter?.CLASSIFICATION || []).length > 0
            ? filter?.CLASSIFICATION.join(",")
            : null,
        class_ids: filter.CLASS_IDS || [],
        fetch_ungrouped_txn_ids:
          filter.NO_CLASSIFICATION.length > 0
            ? filter.NO_CLASSIFICATION.join(",")
            : null,
      },
      {
        skip:
          !groupId ||
          !entityId ||
          !reportType ||
          reportType === SAVED_REPORTS ||
          !filter.START_DATE,
      }
    );

  const { report_data, type } = data || {};
  const { accounts = [] } = report_data || {};

  const isEmpty = !report_data || accounts?.length === 0;

  return (
    <Async.Root {...{ isEmpty, isLoading, isSuccess }}>
      <Async.Empty>
        <StartBookkeepingDateEmptyScreen />
      </Async.Empty>
      <Async.Success>
        <ReportsAccordion.Root className="t-pb-20 t-relative">
          {report_data && (
            <ReportsAccordion.Trigger
              reportType={type!}
              filter={filter}
              isLoading={isFetching}
              openCategory={openComment}
              data={accounts}
              secondaryAction={({ uuid }) => (
                <ConditionalLink
                  to={redirectToCategories({
                    uuid,
                    groupId,
                    entityId,
                    filter,
                    isAdmin,
                  })}
                >
                  <span className="t-text-text-30 hover:t-text-purple t-transition">
                    <Redirect size={12} />
                  </span>
                </ConditionalLink>
              )}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
            />
          )}
        </ReportsAccordion.Root>
        <TransactionSlider />
        <TableSelectionControls
          deselectRows={() => {
            setSelectedRows?.({});
          }}
          allSelectedRows={selectedRows || {}}
          reportType={reportType}
          hasWritePermission={hasWritePermission}
        />
      </Async.Success>
    </Async.Root>
  );
};

const InteractiveReportContent = ({
  reportType,
  entityId,
  settings,
  startDateData,
}: {
  reportType: ReportsType;
  entityId: string;
  settings: BooksLockDateResponse;
  startDateData: {
    start_date: string;
  };
}) => {
  usePageTitle(REPORT_TYPE[reportType!] || "Report");
  const commentPane = useModal();
  const openComment = useAppSelector((store) => store.openComment.openComment);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const query = useQuery();
  const selectedCategoryId = query.get("selected_category_id");
  const openCommentParam = query.get("open_comment");
  const bookkeepingStartDate = startDateData?.start_date;

  const defaultDateRange = getDateRangeWithSetting(
    DATE_PERIOD.CURRENT_TAX_YEAR,
    settings?.tax_year_end,
    bookkeepingStartDate
  );

  const defaultForecastStart = dayjs(defaultDateRange.endDate)
    .add(1, "month")
    .startOf("month")
    .format(YYYY_MM_DD);
  const defaultForecastEnd = dayjs(defaultForecastStart)
    .add(5, "month")
    .endOf("month")
    .format(YYYY_MM_DD);

  const { values: filter, updateFilter } = useFilters<ReportFilterType>({
    initialValue: {
      START_DATE: dayjs(defaultDateRange.startDate).format(YYYY_MM_DD),
      END_DATE: dayjs(defaultDateRange.endDate).format(YYYY_MM_DD),
      ACCOUNTING_METHOD: settings?.accounting_method!,
      VIEW_BY: null,
      HIDE_ZERO: true,
      TXN_GROUP_ID: null,
      CLASS_IDS: [],
      CLASSIFICATION: [],
      NO_CLASSIFICATION: [],
      OTHER: null,
      COMPARE_BY: null,
      VARIANCE: [],
      PERCENTAGE_MODE: null,
      FORECAST_ENABLED: false,
      FORECAST_START: defaultForecastStart,
      FORECAST_END: defaultForecastEnd,
      FORECAST_SET_ID: null,
    },
    useQueryParams: true,
  });

  const setActiveComment = (comment: NoteResponse) => {
    if (comment.category) {
      dispatch(setOpenComment(comment.category.uuid));
    }
  };

  const isGroupByView =
    Boolean(filter.TXN_GROUP_ID) ||
    Boolean(filter.OTHER) ||
    (Boolean(filter.VIEW_BY) && filter.VIEW_BY !== PERIOD.TOTALS) ||
    Boolean(filter.COMPARE_BY);

  const params = {
    reportType: reportType,
    accountingMethod: filter.ACCOUNTING_METHOD,
    startDate: filter.START_DATE ? filter.START_DATE : null,
    endDate: filter.END_DATE ? filter.END_DATE : null,
    showNonZeroAccounts: filter.HIDE_ZERO,
    comparison_report_type: filter.VIEW_BY,
  };

  useEffect(() => {
    // Reset VIEW_BY, TXN_GROUP_ID and CLASS_IDS when report type is changed to other than Income Statement
    if (reportType !== INCOME_STATEMENT) {
      updateFilter("VIEW_BY", null);
      updateFilter("TXN_GROUP_ID", null);
      updateFilter("OTHER", null);
      updateFilter("CLASS_IDS", []);
      updateFilter("CLASSIFICATION", []);
      updateFilter("NO_CLASSIFICATION", []);
      updateFilter("FORECAST_ENABLED", false);
    }
  }, [reportType]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (selectedCategoryId) {
      const currentSearch = parse(location.search, { ignoreQueryPrefix: true });
      delete currentSearch.selected_category_id;
      timeout = setTimeout(() => {
        history.replace(
          `${location.pathname}${stringify(currentSearch, {
            addQueryPrefix: true,
          })}`
        );
      }, 5000);
    }
    return () => clearTimeout(timeout);
  }, [selectedCategoryId]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (openCommentParam) {
      dispatch(setOpenComment(openCommentParam));
      const currentSearch = parse(location.search, { ignoreQueryPrefix: true });
      delete currentSearch.open_comment;
      timeout = setTimeout(() => {
        history.replace(
          `${location.pathname}${stringify(currentSearch, {
            addQueryPrefix: true,
          })}`
        );
      }, 5000);
    }
    return () => clearTimeout(timeout);
  }, [openCommentParam]);

  return (
    <div className="t-flex t-h-full t-pl-5 sm:t-pl-3">
      <div
        className={classNames("t-flex t-flex-col t-w-full t-mb-5", {
          "t-pr-3": commentPane.isOpen,
          "t-pr-10 sm:t-pr-3": !commentPane.isOpen,
          "t-grow t-overflow-hidden sm:t-overflow-visible": isGroupByView,
          "t-grow-0 t-overflow-x-auto sm:t-overflow-x-hidden": !isGroupByView,
        })}
      >
        <h1 className="t-sr-only">{reportType}</h1>
        <ReportTopBar
          openComments={commentPane.toggle}
          reportType={reportType}
          filter={filter}
          updateFilter={updateFilter}
          isGroupByView={isGroupByView}
        />
        <ReportContent
          filter={filter}
          reportType={reportType}
          isGroupByView={isGroupByView}
        />
      </div>
      <AnimatePresence>
        {commentPane.isOpen && (
          <SliderAnimation className="t-h-full t-shrink-0 t-grow-0 t-sticky t-top-0 t-border-0 t-border-l t-border-solid t-border-neutral-0 t-overflow-y-auto t-p-4 t-pt-0 sm:t-fixed sm:t-inset-0 sm:t-z-modal sm:t-bg-surface sm:!t-w-full">
            <CommentsSlider
              setActiveComment={setActiveComment}
              activeComment={openComment}
              onClose={commentPane.toggle}
              commentTypes={[REPORT_COMMENT_TYPE[reportType]]}
              reportFilters={params}
            />
          </SliderAnimation>
        )}
      </AnimatePresence>
    </div>
  );
};

export const InteractiveReport = ({
  reportType,
  entityId,
}: {
  reportType: ReportsType;
  entityId: string;
}) => {
  const { uuid: groupId } = useCurrentGroup();
  const { data: settings, isLoading } = useGetBookkeepingSettingsQuery(
    {
      entityId,
    },
    { skip: !entityId }
  );

  const { data: startDateData } = useGetStartingDateQuery(
    { groupId: groupId!, entityId },
    { skip: !groupId! || !entityId }
  );

  if (!settings || isLoading) {
    return <Loader />;
  }

  return (
    <InteractiveReportContent
      reportType={reportType}
      entityId={entityId}
      settings={settings}
      startDateData={startDateData!}
    />
  );
};
